<script setup lang="ts">
import { DateTime } from 'luxon'
import { Platforms } from '~/lib/models/smartbanners'
import { Smartbanner } from '~/lib/services/content/content.configuaration.dtos'

const props = defineProps<{
  config: Smartbanner
}>()

const isMobile = inject<Ref<boolean>>('isMobile')
const layoutStore = useLayoutStore()

const isAndroid = computed(() => layoutStore.platform === Platforms.ANDROID)
const isIOS = computed(() => layoutStore.platform === Platforms.IOS)

const isVisible = computed(
  () =>
    isMobile?.value && !layoutStore.smartbannerExpirationDate && (isAndroid.value || isIOS.value)
)

const close = () => {
  const utcNowMillis = DateTime.utc().toMillis()
  const millisInDay = 24 * 60 * 60 * 1000
  const timeHiddenMillis = (props.config.cookieExpirationDays || 0) * millisInDay
  const expiration = utcNowMillis + timeHiddenMillis
  const expirationDate = DateTime.fromMillis(expiration, { zone: 'utc' }).toISO()
  if (expirationDate) layoutStore.smartbannerExpirationDate = expirationDate
}

const navigate = async () => {
  const url = isAndroid.value ? props.config.androidUrl : props.config.iosUrl
  close()
  await navigateTo(url, { external: true })
}

onMounted(() => {
  // check if expirationDate is expired
  if (layoutStore.smartbannerExpirationDate) {
    const utcNowMillis = DateTime.utc().toMillis()
    const expirationDateTimeMillis = DateTime.fromISO(layoutStore.smartbannerExpirationDate, {
      zone: 'utc',
    }).toMillis()
    const isExpired = utcNowMillis >= expirationDateTimeMillis
    // must show smartbanner again
    if (isExpired) layoutStore.smartbannerExpirationDate = undefined
  }
})
</script>

<template>
  <Transition
    name="slide-in-fade"
    enter-active-class="slide-in-fade-enter-active"
    enter-to-class="slide-in-fade-enter-to"
    leave-active-class="slide-in-fade-leave-active"
    leave-to-class="slide-in-fade-leave-to"
    appear
  >
    <section
      v-if="isVisible"
      data-test="smartbanner"
      class="flex w-full flex-row items-center gap-3 bg-mcd-smartbannerBg p-4"
    >
      <img
        class="h-8 w-8"
        src="/images/icons/smartbanner/icon-smartbanner.svg"
        alt="McDonalds icon"
      />
      <div class="h-fit flex-1">
        <p class="text-sm font-bold">{{ config.title }}</p>
        <span class="block text-xs">{{ config.description }}</span>
      </div>

      <McdButton is-primary class="rounded-full px-[0.675rem]" @on-click="navigate">
        <img src="/images/icons/smartbanner/download-icon.svg" alt="download icon" />
      </McdButton>

      <CloseIcon @click="close" />
    </section>
  </Transition>
</template>

<style scoped>
.slide-in-fade-enter-active,
.slide-in-fade-leave-active {
  @apply transition-all duration-300;
}
.slide-in-fade-enter-to,
.slide-in-fade-leave {
  @apply translate-y-0 transform opacity-100;
}
.slide-in-fade-enter,
.slide-in-fade-leave-to {
  @apply -translate-y-4 transform opacity-0;
}
</style>
